import DateFnsUtils from '@date-io/date-fns'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import FormHelperText from '@material-ui/core/FormHelperText'
import { PopoverProps } from '@material-ui/core/Popover'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { addMinutes, format } from 'date-fns'
import React, { useEffect, useRef } from 'react'

import {
    MoreFilterContext,
    MoreFilterContextProps,
} from './Context/MoreFilterContext'

interface DateRangeNameValue {
    name: string
    value: number
}
enum dateRangeOptionVal {
    last6Months = 1,
    customRange,
}
const invalidDate: string = 'Invalid Date'
const dateFormat: string = 'yyyy-MM-dd'
const dateRangeOptions = [
    {
        name: 'The Last 6 Months',
        value: 1,
    },
    {
        name: 'Custom Range',
        value: 2,
    },
]
const useStyles = makeStyles((theme: Theme) => ({
    formControl: {
        margin: theme.spacing(2),
        minWidth: 217,
        textTransform: 'capitalize',
    },
    dataRangeCont: {
        padding: '8px 24px',
    },
    dateGridItem: {
        margin: theme.spacing(2),
    },
    datePicker: {
        '& .MuiInputAdornment-positionEnd button.MuiIconButton-root': {
            marginLeft: theme.spacing(3),
        },
        '.MuiAutocomplete-clearIndicator': {
            visibility: 'hidden',
        },
    },
}))

const MoreFilterDateRange: React.FC = () => {
    const classes = useStyles()
    const { handleEmptyInputSelection, emptyInputSelection } = React.useContext<
        MoreFilterContextProps
    >(MoreFilterContext)
    const [
        selectedValue,
        setSelectedValue,
    ] = React.useState<DateRangeNameValue | null>(null)
    const [selectedToDate, setSelectedToDate] = React.useState<
        Date | string | null
    >(null)
    const [selectedFromDate, setSelectedFromDate] = React.useState<
        Date | string | null
    >(null)
    const [fromDateErrMessage, setFromDateErrMessage] = React.useState<boolean>(
        false
    )
    const [toDateErrMessage, setToDateErrMessage] = React.useState<boolean>(
        false
    )

    React.useEffect(() => {
        if (!emptyInputSelection) return
        const clearAll =
            selectedValue !== null &&
            Object.values(emptyInputSelection).filter((d) => d !== null)
                ?.length === 0

        if (emptyInputSelection.lastUpdatedInPastDays) {
            setSelectedValue(dateRangeOptions[0])
        } else if (
            emptyInputSelection.lastUpdatedStartDate &&
            emptyInputSelection.lastUpdatedEndDate
        ) {
            setSelectedValue(dateRangeOptions[1])
            //to date and from date can have three values Date, string and null, setting the state from context only if values are null
            if (selectedToDate !== null && selectedFromDate !== null) return
            const date = new Date(emptyInputSelection.lastUpdatedEndDate)
            const dateStart = new Date(emptyInputSelection.lastUpdatedStartDate)
            setSelectedToDate(addMinutes(date, date?.getTimezoneOffset()))
            setSelectedFromDate(
                addMinutes(dateStart, date?.getTimezoneOffset())
            )
        } //clear all more filter
        else if (clearAll) {
            setSelectedValue(null)
            resetCustomRangeInputs()
        }
    }, [emptyInputSelection, selectedFromDate, selectedToDate])

    const resetCustomRangeInputs = () => {
        setSelectedFromDate(null)
        setSelectedToDate(null)
        setFromDateErrMessage(false)
        setToDateErrMessage(false)
    }
    const handleToDateChange = (date: Date | string | null) => {
        if (isDateChangeValid(date, selectedFromDate)) {
            setToDateErrMessage(true)
        } else {
            setToDateErrMessage(false)
            isSelectedDatesValid(selectedFromDate, date)
        }
        setFromDateErrMessage(false)
        setSelectedToDate(date ?? '')
    }

    const handleFromDateChange = (date: Date | string | null) => {
        if (isDateChangeValid(selectedToDate, date)) {
            setFromDateErrMessage(true)
        } else {
            setFromDateErrMessage(false)
            isSelectedDatesValid(date, selectedToDate)
        }
        setToDateErrMessage(false)
        setSelectedFromDate(date ?? '')
    }

    const disableToDates = (date: Date | null) => {
        return Boolean(
            date &&
                selectedFromDate &&
                isValidDate(selectedFromDate) &&
                compareDates(date, selectedFromDate)
        )
    }

    const disableFromDates = (date: Date | null) => {
        return Boolean(
            date &&
                selectedToDate &&
                isValidDate(selectedToDate) &&
                compareDates(selectedToDate, date)
        )
    }

    const onChange = (
        _event: React.ChangeEvent<{}>,
        newValue: DateRangeNameValue | null
    ) => {
        resetCustomRangeInputs()
        if (newValue && handleEmptyInputSelection) {
            handleEmptyInputSelection({
                lastUpdatedInPastDays:
                    newValue.value !== dateRangeOptionVal.customRange
                        ? '180'
                        : '',
                lastUpdatedStartDate: null,
                lastUpdatedEndDate: null,
            })
        }
        setSelectedValue(newValue)
    }

    const isSelectedDatesValid = (
        fromDate: Date | string | null,
        toDate: Date | string | null
    ) => {
        if (
            toDate &&
            fromDate &&
            handleEmptyInputSelection &&
            isValidDate(toDate) &&
            isValidDate(fromDate)
        ) {
            handleEmptyInputSelection({
                lastUpdatedInPastDays: null,
                lastUpdatedStartDate: getFormattedDate(fromDate),
                lastUpdatedEndDate: getFormattedDate(toDate),
            })
        }
    }
    const handleInputChange = (
        _event: React.ChangeEvent<{}>,
        newInputValue: string | null
    ) => {
        if (
            !newInputValue &&
            selectedValue?.value &&
            handleEmptyInputSelection
        ) {
            handleEmptyInputSelection({
                lastUpdatedInPastDays: null,
                lastUpdatedStartDate: null,
                lastUpdatedEndDate: null,
            })
        }
    }
    const datePickerFrom = useRef<HTMLDivElement>(null)
    const datePickerTo = useRef<HTMLDivElement>(null)
    const PopoverPropsdatePickerFrom: Partial<PopoverProps> = {
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
        },
        anchorEl: datePickerFrom.current,
        transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
        },
    }
    const PopoverPropsdatePickerTo = {
        ...PopoverPropsdatePickerFrom,
        anchorEl: datePickerTo.current,
    }
    useEffect(() => {
        PopoverPropsdatePickerFrom.anchorEl = datePickerFrom.current
        PopoverPropsdatePickerTo.anchorEl = datePickerTo.current
    }, [
        datePickerFrom,
        datePickerTo,
        selectedValue,
        PopoverPropsdatePickerFrom.anchorEl,
        PopoverPropsdatePickerTo.anchorEl,
    ])
    return (
        <GridContainer className={classes.dataRangeCont}>
            <GridItem className={classes.formControl}>
                <Autocomplete
                    data-testid={'date-range-autocomplete'}
                    options={dateRangeOptions}
                    getOptionLabel={(option: DateRangeNameValue) =>
                        option?.name
                    }
                    value={selectedValue}
                    onChange={onChange}
                    inputValue={selectedValue?.name ?? ''}
                    onInputChange={handleInputChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Published In"
                            name="dateRange"
                            variant="outlined"
                            inputProps={{
                                ...params.inputProps,
                                'aria-autocomplete': 'none',
                                autoComplete: 'chrome-off',
                            }}
                        />
                    )}
                    ListboxProps={{ 'data-testid': 'list-box' }}
                />
            </GridItem>
            {selectedValue &&
                selectedValue.value === dateRangeOptionVal.customRange && (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <GridItem
                            className={classes.dateGridItem}
                            ref={datePickerFrom}
                        >
                            <KeyboardDatePicker
                                data-testid={'from-date-picker'}
                                disableFuture={!selectedToDate}
                                disableToolbar
                                format="MM/dd/yyyy"
                                autoOk
                                label="From"
                                className={classes.datePicker}
                                error={
                                    fromDateErrMessage ||
                                    !isValidDate(selectedFromDate)
                                }
                                PopoverProps={PopoverPropsdatePickerFrom}
                                name="lastUpdatedStartDate"
                                value={filterDateString(selectedFromDate)}
                                onChange={handleFromDateChange}
                                inputVariant="outlined"
                                variant="inline"
                                maxDateMessage={null}
                                invalidDateMessage={invalidDate}
                                shouldDisableDate={disableFromDates}
                                initialFocusedDate={null}
                            />
                            {fromDateErrMessage && (
                                <FormHelperText error={fromDateErrMessage}>
                                    {'Invalid Date Range'}
                                </FormHelperText>
                            )}
                        </GridItem>
                        <GridItem
                            ref={datePickerTo}
                            className={classes.dateGridItem}
                        >
                            <KeyboardDatePicker
                                data-testid={'to-date-picker'}
                                disableToolbar
                                format="MM/dd/yyyy"
                                autoOk
                                label="To"
                                className={classes.datePicker}
                                error={
                                    toDateErrMessage ||
                                    !isValidDate(selectedToDate)
                                }
                                PopoverProps={PopoverPropsdatePickerTo}
                                name="lastUpdatedEndDate"
                                value={filterDateString(selectedToDate)}
                                onChange={handleToDateChange}
                                shouldDisableDate={disableToDates}
                                inputVariant="outlined"
                                variant="inline"
                                maxDateMessage={null}
                                invalidDateMessage={invalidDate}
                                initialFocusedDate={null}
                            />
                            {toDateErrMessage && (
                                <FormHelperText error={toDateErrMessage}>
                                    {'Invalid Date Range'}
                                </FormHelperText>
                            )}
                        </GridItem>
                    </MuiPickersUtilsProvider>
                )}
        </GridContainer>
    )
}

const isValidDate = (date: Date | string | null) => {
    return date?.toString() !== invalidDate
}
const filterDateString = (date: Date | string | null) =>
    date !== '' ? date : null
const compareDates = (date1: Date | string, date2: Date | string) => {
    const toDate: Date | string = date1
    const fromDate: Date | string = date2
    return (
        format(new Date(toDate ?? ''), dateFormat) <
        format(new Date(fromDate ?? ''), dateFormat)
    )
}

const getFormattedDate = (date: Date | string) => {
    return format(new Date(date ?? ''), dateFormat)
}

const isDateChangeValid = (
    toDate: Date | string | null,
    fromDate: Date | string | null
) => {
    return (
        toDate &&
        fromDate &&
        isValidDate(fromDate) &&
        isValidDate(toDate) &&
        compareDates(toDate, fromDate)
    )
}

export default MoreFilterDateRange
